



















































import { defineComponent, nextTick, onMounted, provide, ref, Ref, watch, } from '@nuxtjs/composition-api';
import { SfAccordion, SfButton, SfFilter, SfHeading, SfRadio, } from '@storefront-ui/vue';

import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { useUiHelpers } from '~/composables';
import { getFilterConfig, isFilterEnabled } from '~/modules/catalog/category/config/FiltersConfig';
import PictimeSelectedFilters from '~/modules/catalog/category/components/filters/FiltersSidebar/PictimeSelectedFilters.vue';
import { useProductFilter } from '~/modules/catalog/category/components/filters/command/useProductFilter';
import type { Aggregation } from '~/modules/GraphQL/types';
import type { SelectedFiltersInterface } from './useFilters';
import { useFilters } from './useFilters';

import PictimeSidebar from '~/components/organisms/PictimeSidebar';

export interface UseFiltersProviderInterface {
  selectedFilters: Ref<SelectedFiltersInterface>,
  filters: Ref<Aggregation[]>,
}

export default defineComponent({
  name: 'PictimeFilters',
  components: {
    PictimeSelectedFilters,
    SkeletonLoader,
    CheckboxType: () => import('~/modules/catalog/category/components/filters/renderer/PictimeCheckboxType.vue'),
    SwatchColorType: () => import('~/modules/catalog/category/components/filters/renderer/SwatchColorType.vue'),
    RadioType: () => import('~/modules/catalog/category/components/filters/renderer/RadioType.vue'),
    YesNoType: () => import('~/modules/catalog/category/components/filters/renderer/YesNoType.vue'),
    PictimeSidebar,
    SfHeading,
    SfAccordion,
    SfFilter,
    SfButton,
    SfRadio,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    catUid: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { changeFilters, clearFilters } = useUiHelpers();
    const removableFilters = ref([]);
    const filters = ref<Aggregation[]>([]);
    const isLoading = ref(true);

    const uiHelpers = useUiHelpers();
    const { search: getProductFilters } = useProductFilter();

    const {
      selectedFilters, selectFilter, removeFilter, isFilterSelected, getRemovableFilters,
    } = useFilters();

    const updateRemovableFilters = () => {
      removableFilters.value = getRemovableFilters(filters.value, selectedFilters.value);
    };

    const doApplyFilters = () => {
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      reloadFilters();
      if (window?.scroll) {
        window.scroll(0, 0);
      }
      emit('reloadSelectedFilters', removableFilters.value.length);
      emit('reloadProducts');
      emit('close');
    };

    const doRemoveFilter = ({ id, value }: { id: string, value: string }) => {
      removeFilter(id, value);
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      reloadFilters();
      emit('reloadSelectedFilters', removableFilters.value.length);
      emit('reloadProducts');
      emit('close');
    };

    const doClearFilters = () => {
      clearFilters(false);
      selectedFilters.value = {};
      updateRemovableFilters();
      reloadFilters();
      emit('reloadSelectedFilters', removableFilters.value.length);
      emit('reloadProducts');
      emit('close');
    };

    const selectAndApplyFilters = async (filter, event) => {
      selectFilter(filter, event);
      doApplyFilters();
    };

    const hasSelectedOption = (filter) => {
      let res = false;
      Object.keys(filter.options).forEach((option) => {
        if (Boolean(isFilterSelected(filter.attribute_code, filter.options[option].value))) {
          res = true;
        }
      });
      return res;
    };

    watch(() => props.isVisible, (newValue) => {
      // disable Storefrontt UI's body scroll lock which is launched when :visible prop on SfSidebar changes
      // two next ticks because SfSidebar uses nextTick aswell, and we want to do something after that tick.
      if (newValue) {
        nextTick(() => nextTick(() => clearAllBodyScrollLocks()));
      }
    });

    watch(() => props.catUid, (newValue) => {
      if (newValue) {
        nextTick(() => nextTick(() => reloadFilters()));
      }
    });

    onMounted(async () => {
      await reloadFilters();
      updateRemovableFilters();
    });

    const reloadFilters = async () => {
      let filtersPrams = { ...uiHelpers.getFacetsFromURL() };
      let baseFilterParams = { category_uid: { eq: props.catUid } };
      let additionalParams = {};

      Object.entries(filtersPrams.filters).forEach((filter) => {
        let res = { [filter[0]]: { in: filter[1] } };
        additionalParams = Object.assign(additionalParams, res);
      });

      let extendedFilterParams = {};
      if (Object.keys(additionalParams).length !== 0) {
        extendedFilterParams = { filter: Object.assign(baseFilterParams, additionalParams), search: filtersPrams.term };
      } else {
        extendedFilterParams = { filter: baseFilterParams, search: filtersPrams.term };
      }

      const [loadedFilters] = await Promise.all([getProductFilters(extendedFilterParams)]);

      filters.value = loadedFilters.filter((filter) => isFilterEnabled(filter.attribute_code));
      isLoading.value = false;
    };

    provide('UseFiltersProvider', { isFilterSelected, selectedFilters, filters });

    const countAvailables = (filters) => {
      let count = 0;
      Object.keys(filters).forEach((filter) => {
        if (filters[filter].attribute_code !== 'flag_promo' && filters[filter].attribute_code !== 'is_new_product' && filters[filter].options && filters[filter].options.length > 1) {
          count++;
        }
      });
      return count;
    };

    return {
      selectFilter,
      selectAndApplyFilters,
      doApplyFilters,
      doRemoveFilter,
      doClearFilters,
      getFilterConfig,
      selectedFilters,
      filters,
      isLoading,
      removableFilters,
      hasSelectedOption,
      countAvailables,
    };
  },
});
